import { useEffect, useMemo, useState } from 'react'
import { useEventTimetable, useDeleteService } from '../hooks/useServices'
import { useOperatingDays } from '../hooks/useScheduleV2'
import { useArchived } from '../contexts/ArchiveContext'
import { ServiceEditor } from './ServiceEditor'
import { Icon } from './Icon'
import type { Service, EventListItem, TimetableRow } from '../types'

// ScheduleTab — per-event train services / daily timetable.
//
// Layout:
//   1. Header + MERAC-source banner (when source='merac').
//   2. Day-selector chip strip (filter; shown when there are ≥ 2
//      operating days to choose between).
//   3. Train Services — Daily Timetable table. Filtered by the day
//      chip. Rows whose `date` is null (manual events with one
//      shared timetable) stay visible regardless of selection.
//   4. Train Turns — locos grouped by their service sequence,
//      filtered alongside the table.
//
// Rules of Hooks discipline (post-revert of PR #24): EVERY hook lives
// above any conditional return. The previous version interleaved
// `if (isLoading) return …` between hooks, which crashes with React
// error #310 ("rendered more hooks than during the previous render")
// the moment data arrives. Day selector data comes from operating-days
// (canonical) with row-date distinct values as fall-back; both
// computations defensively handle undefined data so the hooks always
// run regardless of fetch state.

interface Props {
  event: EventListItem
}

export function ScheduleTab({ event }: Props) {
  const eventId = event.id
  const archived = useArchived()

  // -------- Hooks (every one of these must run on every render) ---------
  const { data: timetable, isLoading, error } = useEventTimetable(eventId)
  const { data: operatingDays } = useOperatingDays(eventId)
  const deleteMut = useDeleteService(eventId)

  const [editing, setEditing] = useState<Service | null>(null)
  const [creating, setCreating] = useState(false)
  const [selectedDay, setSelectedDay] = useState<string | null>(null)

  // The full row list — null/undefined-safe so this memo can run
  // before the fetch resolves. Same defensive shape for `source`.
  const list = useMemo<TimetableRow[]>(
    () => timetable?.services ?? [],
    [timetable]
  )
  const source: 'manual' | 'merac' = timetable?.source ?? 'manual'

  // Available days for the chip strip. Priority: dbo.OperatingDays
  // (where running=true) → distinct row dates → empty.
  const availableDays = useMemo<string[]>(() => {
    const fromOps = (operatingDays ?? [])
      .filter((d) => d.running)
      .map((d) => d.date)
    if (fromOps.length > 0) return Array.from(new Set(fromOps)).sort()
    const fromRows = list.map((r) => r.date).filter((d): d is string => d != null)
    return Array.from(new Set(fromRows)).sort()
  }, [operatingDays, list])

  // Default-select once available days are known: today if it falls
  // in the event window, otherwise the first day. Null means
  // "All days" (only when there are no per-day dates to pick).
  useEffect(() => {
    if (selectedDay != null) return
    if (availableDays.length === 0) return
    const today = isoDateUTC(new Date())
    const startsBy = availableDays[0]!
    const endsBy = availableDays[availableDays.length - 1]!
    setSelectedDay(today >= startsBy && today <= endsBy ? today : startsBy)
  }, [availableDays, selectedDay])

  // Filtered rows. Rows with date=null stay visible (manual events
  // with one shared timetable) so the strip degrades gracefully.
  const filtered = useMemo<TimetableRow[]>(() => {
    if (selectedDay == null) return list
    return list.filter((r) => r.date == null || r.date === selectedDay)
  }, [list, selectedDay])

  // -------- Early-return paths (all below the hooks above) ---------
  if (isLoading) {
    return <div className="text-ink-soft text-sm font-mono">Loading services…</div>
  }
  if (error) {
    return (
      <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
        <div className="font-semibold mb-1">Could not load services</div>
        <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
      </div>
    )
  }

  // -------- Derived values + JSX ---------
  const serviceCount = list.length
  const filteredCount = filtered.length
  const isMerac = source === 'merac'
  const showDaySelector = availableDays.length >= 2

  // Manual rows have a numeric id and the full Service shape; MERAC
  // rows have a string id. The editor only opens for manual rows
  // (MERAC source is server-derived and read-only).
  function openEditorFor(row: TimetableRow) {
    if (row.source !== 'manual' || typeof row.id !== 'number') return
    setEditing({
      id: row.id,
      eventId,
      serviceLabel: row.serviceLabel,
      timeText: row.timeText,
      loco: row.loco,
      originStation: row.originStation,
      destinationStation: row.destinationStation,
      capacity: row.capacity,
      notes: row.notes,
      sortOrder: row.sortOrder,
      crew: row.crew
    })
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h2 className="font-display text-xl">Schedule</h2>
        <p className="text-sm text-ink-soft mt-0.5">
          {isMerac
            ? `Train timetable for this event is sourced from MERAC (event code ${timetable?.meracEventId ?? ''}). Rows refresh automatically as MERAC sessions sync.`
            : 'Train services and the daily timetable for this event. Click a row to edit.'}
        </p>
      </div>

      {/* MERAC source banner */}
      {isMerac && (
        <div
          className="rounded p-3 flex items-start gap-3 text-sm"
          style={{ background: 'linear-gradient(135deg, #e0e7ec 0%, #f0f5fb 100%)' }}
        >
          <Icon name="train" size={16} className="text-slate flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold">MERAC-sourced timetable</div>
            <div className="text-xs text-ink-soft mt-0.5">
              These services are derived from <code className="font-mono text-[11px]">dbo.MeracSessions</code>{' '}
              via the <code className="font-mono text-[11px]">MeracEventMap</code> binding for{' '}
              <span className="font-mono">{timetable?.meracEventId}</span>. Label, route, capacity and
              booked counts are read straight from the MERAC session header. Loco isn&apos;t carried on
              MERAC rows so that column renders as &mdash;.
            </div>
          </div>
        </div>
      )}

      {/* Day-selector chip strip — filters the services table to one
          operating day. Only renders when there are ≥ 2 days to pick
          between; single-day events get no chip strip. Rendered inline
          here (rather than as a separate component) to keep the hook
          surface contained — this whole tab uses hooks above any
          conditional return, and a child component with the same rule
          adds one more place for the rule to break. */}
      {showDaySelector && (
        <div
          className="flex flex-wrap items-center gap-2"
          role="tablist"
          aria-label="Filter timetable by operating day"
        >
          <DayChip
            label="All days"
            active={selectedDay === null}
            onClick={() => setSelectedDay(null)}
          />
          {availableDays.map((iso) => (
            <DayChip
              key={iso}
              label={formatChipLabel(iso)}
              active={selectedDay === iso}
              isToday={iso === isoDateUTC(new Date())}
              onClick={() => setSelectedDay(iso)}
            />
          ))}
        </div>
      )}

      {/* Card 1: Services table */}
      <div className="bg-white border border-line rounded">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-line">
          <div className="flex-1">
            <div className="text-sm font-semibold">Train Services &mdash; Daily Timetable</div>
            <div className="text-xs text-ink-soft mt-0.5">
              {isMerac
                ? 'Read-only view of MERAC-bound sessions. To edit, manage in MERAC directly.'
                : 'Booking counts will appear here once ticket-sales sync ships. Crew assignment moves over once the Roster tab is live.'}
            </div>
          </div>
          {!isMerac && !archived && (
            <>
              <button
                type="button"
                disabled
                title="WTT import is coming in a later phase."
                className="text-xs font-semibold text-ink-soft border border-line rounded px-3 py-1.5 cursor-not-allowed opacity-60"
              >
                Import WTT
              </button>
              <button
                type="button"
                onClick={() => setCreating(true)}
                className="text-xs font-semibold text-parchment bg-green hover:bg-green/90 rounded px-3 py-1.5 transition-colors"
              >
                + Add Service
              </button>
            </>
          )}
        </div>

        {filteredCount === 0 ? (
          <div className="px-4 py-8 text-center">
            <p className="text-sm text-ink-soft mb-3">
              {serviceCount === 0
                ? (isMerac
                  ? 'No MERAC sessions for this event yet. Once the ontrack .ps1 fires and the normaliser picks up the rows, services will appear here.'
                  : 'No services yet. Add the first service to start building the timetable.')
                : `No services on ${selectedDay ? formatDayLong(selectedDay) : 'the selected day'}.`}
            </p>
            {serviceCount === 0 && !isMerac && !archived && (
              <button
                type="button"
                onClick={() => setCreating(true)}
                className="text-sm text-green font-semibold hover:underline"
              >
                Add the first service
              </button>
            )}
            {serviceCount > 0 && selectedDay != null && (
              <button
                type="button"
                onClick={() => setSelectedDay(null)}
                className="text-sm text-green font-semibold hover:underline"
              >
                Show all days
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-ink-soft border-b border-line">
                  <th className="px-4 py-2 font-semibold w-20">Time</th>
                  <th className="px-4 py-2 font-semibold">Service</th>
                  <th className="px-4 py-2 font-semibold">Loco</th>
                  <th className="px-4 py-2 font-semibold">From &rarr; To</th>
                  <th className="px-4 py-2 font-semibold w-20 text-right">Capacity</th>
                  <th className="px-4 py-2 font-semibold w-32">
                    {isMerac ? 'Booked' : 'Booking'}
                  </th>
                  <th className="px-4 py-2 font-semibold">
                    <span className="inline-flex items-center gap-1">
                      Crew
                      <span
                        className="text-ink-soft cursor-help"
                        title="Crew chips show people rostered to operational roles (Controller/MOM, Duty Manager, Station Foreman) on this event's start day. Edit on the Roster tab."
                      >
                        <Icon name="info" size={12} />
                      </span>
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <ServiceRow
                    key={s.id}
                    row={s}
                    onClick={s.source === 'manual' && !archived ? () => openEditorFor(s) : null}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Card 2: Train Turns — filters with the same day selection so
          a single-day view shows that day's turns, not the event total. */}
      <TrainTurnsCard services={filtered} />

      {!archived && creating && (
        <ServiceEditor
          eventId={eventId}
          service={null}
          onClose={() => setCreating(false)}
        />
      )}

      {!archived && editing && (
        <ServiceEditor
          eventId={eventId}
          service={editing}
          deleting={deleteMut.isPending}
          onClose={() => setEditing(null)}
          onDelete={() => {
            const ok = window.confirm(
              `Delete "${editing.serviceLabel}" (${editing.timeText})? This can't be undone.`
            )
            if (!ok) return
            deleteMut.mutate(editing.id, {
              onSuccess: () => setEditing(null)
            })
          }}
        />
      )}
    </div>
  )
}

// ---- Day chip --------------------------------------------------------------

function DayChip({
  label, active, isToday, onClick
}: {
  label: string
  active: boolean
  isToday?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      role="tab"
      aria-selected={active}
      className={
        'inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full border transition-colors ' +
        (active
          ? 'bg-green text-parchment border-green'
          : 'bg-white text-ink-soft border-line hover:bg-parchment hover:text-ink')
      }
    >
      {label}
      {isToday && !active && (
        <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-green" title="Today" />
      )}
    </button>
  )
}

// ---- Service row -----------------------------------------------------------

function ServiceRow({
  row, onClick
}: {
  row: TimetableRow
  onClick: (() => void) | null
}) {
  const route =
    row.originStation && row.destinationStation
      ? `${row.originStation} → ${row.destinationStation}`
      : row.originStation || row.destinationStation || '—'

  const clickable = onClick != null

  return (
    <tr
      onClick={onClick ?? undefined}
      className={
        'border-b border-line last:border-b-0 transition-colors ' +
        (clickable ? 'hover:bg-parchment cursor-pointer' : '')
      }
    >
      <td className="px-4 py-3 font-mono font-semibold">{row.timeText}</td>
      <td className="px-4 py-3 font-semibold">{row.serviceLabel}</td>
      <td className="px-4 py-3">
        {row.loco
          ? <span className="text-ink">{row.loco}</span>
          : <span className="italic text-ink-soft">—</span>}
      </td>
      <td className="px-4 py-3 text-ink-soft">{route}</td>
      <td className="px-4 py-3 text-right font-mono">
        {row.capacity != null
          ? row.capacity.toLocaleString()
          : <span className="italic text-ink-soft">—</span>}
      </td>
      <td className="px-4 py-3 font-mono">
        {row.meracBooked != null
          ? <span className="text-green font-semibold">{row.meracBooked.toLocaleString()}</span>
          : <span className="italic text-ink-soft text-xs">—</span>}
      </td>
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        <CrewChips crew={row.crew ?? []} />
      </td>
    </tr>
  )
}

function CrewChips({ crew }: { crew: NonNullable<Service['crew']> }) {
  if (crew.length === 0) {
    return <span className="italic text-ink-soft text-xs">No crew assigned</span>
  }
  const visible = crew.slice(0, 4)
  const extra = crew.length - visible.length
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {visible.map((c) => (
        <span
          key={c.userId}
          title={`${c.name} — ${c.roleName}`}
          className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-soft text-slate text-[10px] font-semibold"
        >
          {c.avatarInitials}
        </span>
      ))}
      {extra > 0 && (
        <span
          title={crew.slice(4).map((c) => `${c.name} (${c.roleName})`).join(', ')}
          className="text-[11px] text-ink-soft font-mono"
        >
          +{extra}
        </span>
      )}
    </div>
  )
}

// ---- Train Turns card ------------------------------------------------------

function TrainTurnsCard({ services }: { services: TimetableRow[] }) {
  const turns = useMemo(() => buildTurns(services), [services])

  return (
    <div className="bg-white border border-line rounded">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-line">
        <div className="flex-1">
          <div className="text-sm font-semibold">Train Turns</div>
          <div className="text-xs text-ink-soft mt-0.5">
            Locomotives grouped by their service sequence. Booked counts,
            coaling stops, and crew conflict detection arrive when MERAC
            sync and the operational sign-on data land.
          </div>
        </div>
        <span className="text-xs text-ink-soft font-mono">
          {turns.length} turn{turns.length === 1 ? '' : 's'}
        </span>
      </div>

      {turns.length === 0 ? (
        <div className="px-4 py-6 text-sm text-ink-soft italic">
          {services.length === 0
            ? 'No services yet — add services above to see them grouped by locomotive here.'
            : 'No locomotives assigned to any service yet. Edit a row above and pick a loco to start building turns.'}
        </div>
      ) : (
        <div className="divide-y divide-line">
          {turns.map((t, i) => (
            <TurnRow key={t.loco ?? `unassigned-${i}`} turn={t} />
          ))}
        </div>
      )}
    </div>
  )
}

interface Turn {
  loco: string | null
  services: TimetableRow[]
  earliestTime: string
}

function buildTurns(services: TimetableRow[]): Turn[] {
  if (services.length === 0) return []
  const byLoco = new Map<string, TimetableRow[]>()
  for (const s of services) {
    const key = s.loco?.trim() ? s.loco.trim() : '__unassigned__'
    const arr = byLoco.get(key) ?? []
    arr.push(s)
    byLoco.set(key, arr)
  }
  return Array.from(byLoco.entries())
    .map(([key, list]) => ({
      loco: key === '__unassigned__' ? null : key,
      services: [...list].sort((a, b) => a.timeText.localeCompare(b.timeText)),
      earliestTime: list.reduce(
        (min, s) => (s.timeText < min ? s.timeText : min),
        list[0]!.timeText
      )
    }))
    .sort((a, b) => {
      if (a.loco == null && b.loco != null) return 1
      if (b.loco == null && a.loco != null) return -1
      return a.earliestTime.localeCompare(b.earliestTime)
    })
}

function TurnRow({ turn }: { turn: Turn }) {
  const firstService = turn.services[0]!
  const lastService = turn.services[turn.services.length - 1]!
  const route =
    firstService.originStation && lastService.destinationStation
      ? `${firstService.originStation} ↔ ${lastService.destinationStation}`
      : null

  return (
    <div className="px-4 py-3.5">
      <div className="flex items-baseline gap-3 mb-2">
        <Icon name="train" size={15} className="text-slate" />
        <div className="font-semibold flex-1">
          {turn.loco ?? <span className="italic text-ink-soft">Unassigned (no loco set)</span>}
        </div>
        <span className="text-[11px] text-ink-soft font-mono">
          {turn.services.length} service{turn.services.length === 1 ? '' : 's'}
          {' · '}
          {firstService.timeText}–{lastService.timeText}
          {route && <span className="ml-2 text-ink-soft">· {route}</span>}
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {turn.services.map((s) => (
          <span
            key={s.id}
            title={`${s.serviceLabel} · ${s.timeText} · ${s.originStation ?? '?'} → ${s.destinationStation ?? '?'}${s.capacity != null ? ` · capacity ${s.capacity}` : ''}`}
            className="inline-flex items-center gap-1 text-[11px] font-mono px-2 py-0.5 rounded bg-parchment border border-line"
          >
            <span className="font-semibold">{s.timeText}</span>
            <span className="text-ink-soft">{s.serviceLabel}</span>
          </span>
        ))}
      </div>
    </div>
  )
}

// ---- Date helpers ----------------------------------------------------------

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const WEEKDAY_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

// "Thu 11 Jun" — short form used for chips.
function formatChipLabel(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return iso
  const date = new Date(Date.UTC(y, m - 1, d))
  return `${WEEKDAY_SHORT[date.getUTCDay()]} ${d} ${MONTH_SHORT[m - 1]}`
}

// "Thursday 11 June" — long form used in the day-filtered empty state.
function formatDayLong(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return iso
  const date = new Date(Date.UTC(y, m - 1, d))
  return `${WEEKDAY_LONG[date.getUTCDay()]} ${d} ${MONTH_LONG[m - 1]}`
}

function isoDateUTC(d: Date): string {
  const y = d.getUTCFullYear()
  const m = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}
